<template>
  <div
    ref="editorContainer"
    @click="emitClick"
    class="quill-editor text_color"
    :class="{ '-mt-8': isLinkedInStep }"
    :style="{ height: `${props.height}px` }"
  ></div>

  <div class="flex flex-col w-full h-fit">
    <div v-if="attachments?.length" class="flex flex-row gap-1 flex-wrap p-2">
      <AttachedItem
        v-for="(file, index) in attachments"
        :file="file"
        :key="`${index}-${file.id}`"
        :downloadable="false"
        @remove="handleFileDelete"
        :download-api="downloadApi"
      />
    </div>

    <CharacterLimitDisplay
      :enableMaxLimit="enableMaxLimit"
      :charCount="charCount"
      :maxLimit="maxLimit"
      :cautionLimit="cautionLimit"
      :cautionText="cautionText"
    />

    <ImageInsertModal
      v-model="showImageModal"
      v-model:imageUrl="imageUrl"
      :imageUrlError="imageUrlError"
      @confirm="confirmImageInsert"
      @cancel="handleImageModalCancel"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed } from "vue";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import DOMPurify from "dompurify";
import {
  highlightExceededText as utilHighlightExceededText,
  createDebouncedHighlight,
  updateCharCount as utilUpdateCharCount,
  normalizeQuillListsContainer,
} from "@/src/utils/quillUtils.js";
import AttachedItem from "@/src/components/attachment_components/AttachedItem.vue";
import QuillResize from "quill-resize-module";
import ImageInsertModal from "@/src/components/quill_editor/ImageInsertModal.vue";
import CharacterLimitDisplay from "@/src/components/quill_editor/CharacterLimitDisplay.vue";
import { uploadDownloadAPITypes } from "@/src/constants/commonConstants.js";

import { generateThemeImageTag } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import store from "@/src/store/index.js";
import DashboardService from "@/src/api/DashboardService.js";

const props = defineProps({
  modelValue: { type: String, default: "" },
  onblur: { type: Function, default: () => {}, required: false },
  height: { type: Number, default: 300 },
  customToolbar: { type: Array, default: null },
  autoFocus: { type: Boolean, default: false },
  downloadApi: { type: String, default: uploadDownloadAPITypes.ATTACHMENTS },
  attachments: { type: Array, default: () => [] },
  placeholder: { type: String, default: "" },
  maxLimit: { type: Number, default: 300 },
  enableMaxLimit: { type: Boolean, default: false },
  cautionLimit: { type: Number, default: 200 },
  cautionText: { type: String, default: "" },
  isLinkedInStep: { type: Boolean, default: false },
  disableImageLinking: { type: Boolean, default: false },
});

const effectiveTheme = computed(() => store.getters["theme/getEffectiveTheme"]);

const Delta = Quill.import("delta");
const BlockEmbed = Quill.import("blots/block/embed");
const LinkBlot = Quill.import("formats/link");
const icons = Quill.import("ui/icons");

icons.bold = generateThemeImageTag({
  icon: imageNames.BOLD_WHITE,
  alt: "Bold",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.italic = generateThemeImageTag({
  icon: imageNames.ITALIC_WHITE,
  alt: "Italic",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.underline = generateThemeImageTag({
  icon: imageNames.UNDERLINE_WHITE,
  alt: "Underline",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.link = generateThemeImageTag({
  icon: imageNames.LINK_WHITE,
  alt: "Link",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.blockquote = generateThemeImageTag({
  icon: imageNames.QUOTE_WHITE,
  alt: "block quote",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.image = generateThemeImageTag({
  icon: imageNames.IMAGE_WHITE,
  alt: "image",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.clean = generateThemeImageTag({
  icon: imageNames.CLEAR_FORMATTING_WHITE,
  alt: "clear formatting",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.list = {};
icons["list"]["ordered"] = generateThemeImageTag({
  icon: imageNames.NUMBERED_LIST_WHITE,
  alt: "numbered List",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons["list"]["bullet"] = generateThemeImageTag({
  icon: imageNames.LIST_WHITE,
  alt: "bullet List",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.align = {};
icons.align[""] = generateThemeImageTag({
  icon: imageNames.LEFT_ALIGN_WHITE,
  alt: "align left",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.align["center"] = generateThemeImageTag({
  icon: imageNames.ALIGN_CENTER_WHITE,
  alt: "align center",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.align["right"] = generateThemeImageTag({
  icon: imageNames.RIGHT_ALIGN_WHITE,
  alt: "align right",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});
icons.align["justify"] = generateThemeImageTag({
  icon: imageNames.JUSTIFY_WHITE,
  alt: "justify",
  imgClass: "size-5",
  theme: effectiveTheme.value,
});

class CustomLink extends LinkBlot {
  static create(value) {
    const node = super.create(value);
    node.setAttribute("href", this.sanitize(value));
    node.setAttribute("rel", "noopener noreferrer");
    node.setAttribute("target", "_blank");
    node.setAttribute("class", "custom_link");
    return node;
  }

  format(name, value) {
    if (name !== this.statics.blotName || !value) {
      super.format(name, value);
    } else {
      this.domNode.setAttribute("href", this.constructor.sanitize(value));
      this.domNode.setAttribute("target", "_blank");
      this.domNode.setAttribute("rel", "noopener noreferrer");
      this.domNode.setAttribute("class", "custom_link");
    }
  }
}

class ImageBlot extends BlockEmbed {
  static blotName = "image";
  static tagName = "img";

  static create(value) {
    const node = super.create();
    if (typeof value === "string") {
      // Quill passes a plain string for clipboard-pasted images
      node.setAttribute("src", value);
      node.setAttribute("alt", "image");
    } else {
      node.setAttribute("alt", value.alt || "image");
      node.setAttribute("src", value.url || "");
      if (value.width) node.setAttribute("width", value.width);
      if (value.height) node.setAttribute("height", value.height);
    }
    return node;
  }

  static value(node) {
    const value = {
      alt: node.getAttribute("alt"),
      url: node.getAttribute("src"),
      width: node.getAttribute("width"),
      height: node.getAttribute("height"),
    };
    return value;
  }
}

class VideoEmbedBlot extends BlockEmbed {
  static blotName = "videoEmbed";
  static tagName = "div";
  static className = "video-embed";

  static create(value) {
    const node = super.create();
    node.innerHTML = `
      <a href="${value.link}" target="_blank" style="display: block; text-decoration: none; max-width: 100%;" class="video-container">
        <span style="color: #007bff; text-decoration: underline; font-size: 13px;">Watch the Video</span>
        <img
          src="${value.thumbnail}"
          alt="${value.title}"
          style="width: 100%; max-width: 100%; height: auto; object-fit: contain; display: block; border: none; border-radius: 8px; margin-top: 4px;"
        />
      </a>
    `;
    return node;
  }

  static value(node) {
    const anchor = node.querySelector("a");
    const img = node.querySelector("img");
    const title = node.querySelector("p");
    return {
      link: anchor?.href || "",
      thumbnail: img?.src || "",
      title: title?.textContent || "",
    };
  }
}

Quill.register(ImageBlot);
Quill.register("modules/resize", QuillResize);
Quill.register("formats/link", CustomLink, true);
Quill.register(VideoEmbedBlot);

const emit = defineEmits([
  "update:modelValue",
  "editor-click",
  "remove-attachment",
]);

const emitClick = () => {
  emit("editor-click");
};

const showImageModal = ref(false);
const imageUrl = ref("");
const imageUrlError = ref("");
const editorContainer = ref(null);
const charCount = ref(0);
let quill;
const isHighlighting = ref(false);
let debouncedHighlightExceededText;

const updateCharCount = () => {
  utilUpdateCharCount(quill, charCount);
};

const highlightExceededText = () => {
  utilHighlightExceededText(quill, {
    maxLimit: props.maxLimit,
    enableMaxLimit: props.enableMaxLimit,
    isHighlighting: isHighlighting,
  });
};

const sanitizeHtml = (html) => {
  const tempDiv = document.createElement("div");
  let cleanHtml = DOMPurify.sanitize(html, {
    ADD_TAGS: ["img", "a", "div"],
    ADD_ATTR: [
      "href",
      "target",
      "width",
      "height",
      "class",
      "style",
      "src",
      "alt",
    ],
    ALLOW_DATA_ATTR: true,
  });

  tempDiv.innerHTML = cleanHtml;

  tempDiv.querySelectorAll("img").forEach((img) => {
    const style = img.getAttribute("style") || "";
    const width = img.getAttribute("width");
    const height = img.getAttribute("height");
    const widthMatch = style.match(/width:\s*(\d+)px/);
    const heightMatch = style.match(/height:\s*(\d+)px/);

    if (widthMatch && !width) {
      img.setAttribute("width", widthMatch[1]);
    }
    if (heightMatch && !height) {
      img.setAttribute("height", heightMatch[1]);
    }

    if (width && height) {
      img.style.width = `${width}px`;
      img.style.height = `${height}px`;
    }

    const computedStyle = window.getComputedStyle(img);
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    if (naturalWidth && naturalHeight) {
      const aspectRatio = naturalHeight / naturalWidth;
      if (width) {
        const calculatedWidth = parseInt(width);
        const calculatedHeight = Math.round(calculatedWidth * aspectRatio);
        img.style.height = `${calculatedHeight}px`;
        img.setAttribute("height", calculatedHeight);
      }
    }

    img.removeAttribute("style");
  });

  tempDiv.querySelectorAll("p, h1, h2, h3, h4, h5, h6").forEach((el) => {
    el.style.margin = "0";
  });

  tempDiv.querySelectorAll(".video-embed, .video-container").forEach((el) => {
    if (el.hasAttribute("style")) {
      el.setAttribute("data-preserved-style", el.getAttribute("style"));
    }
  });

  normalizeQuillListsContainer(tempDiv);

  return tempDiv.innerHTML;
};

const handleImageModalCancel = () => {
  imageUrl.value = "";
  imageUrlError.value = "";
};

const insertTextAtCursor = (text) => {
  if (quill) {
    const range = quill.getSelection();
    if (range) {
      quill.insertText(range.index, text);
    } else {
      quill.insertText(quill.selection.savedRange.index, text);
    }
    emit(
      "update:modelValue",
      props.isLinkedInStep
        ? quill.getText().trim()
        : sanitizeHtml(quill.root.innerHTML),
    );
    updateCharCount();
  }
};

const insertHtmlContent = (htmlContent) => {
  if (!quill) return;

  const range = quill.getSelection(true) || { index: quill.getLength() };

  quill.clipboard.dangerouslyPasteHTML(range.index, htmlContent);
  quill.setSelection(range.index + 1);
  emit("update:modelValue", sanitizeHtml(quill.root.innerHTML));
  updateCharCount();
};

const insertVideoEmbed = (videoData) => {
  if (!quill) return;

  const range = quill.getSelection(true) || { index: quill.getLength() };

  const delta = new Delta()
    .retain(range.index)
    .insert({ videoEmbed: videoData });

  quill.updateContents(delta);
  quill.setSelection(range.index + 1);
  emit("update:modelValue", sanitizeHtml(quill.root.innerHTML));
  updateCharCount();
};

const confirmImageInsert = (urls) => {
  if (Array.isArray(urls)) {
    urls.forEach((url) => {
      if (isValidUrl(url)) {
        insertImage(url);
      }
    });
  } else {
    if (isValidUrl(urls)) {
      insertImage(urls);
    } else {
      imageUrlError.value = "Please enter a valid URL";
      return;
    }
  }

  showImageModal.value = false;
  imageUrl.value = "";
  imageUrlError.value = "";
};

const getPlainText = () => {
  if (quill) {
    return quill.getText().trim();
  }
  return "";
};

const handleFileDelete = (id) => {
  emit("remove-attachment", id);
};

const isValidUrl = (urlString) => {
  try {
    new URL(urlString);
    return true;
  } catch (e) {
    return false;
  }
};

const insertImage = (url, alt = "") => {
  const range = quill.getSelection(true) ?? { index: quill.getLength(), length: 0 };
  quill.insertEmbed(range.index, "image", {
    alt: alt || "image",
    url: url,
    width: 300,
  });
  quill.setSelection(range.index + 1);
  emit("update:modelValue", sanitizeHtml(quill.root.innerHTML));
};

onMounted(() => {
  if (!quill) {
    // Helper function to wrap or update image link
    const wrapImageWithLink = (img, href) => {
      // Check if image is already wrapped in a link (check all ancestors, not just parent)
      const existingAnchor = img.closest('a');
      
      if (existingAnchor) {
        // Update existing link
        existingAnchor.href = href;
        existingAnchor.target = '_blank';
        existingAnchor.rel = 'noopener noreferrer';
      } else {
        // Create new anchor wrapper
        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.style.cursor = 'pointer';
        
        // Wrap image with anchor
        img.parentNode.insertBefore(anchor, img);
        anchor.appendChild(img);
      }
      
      // Trigger Quill's change detection by emitting text-change event
      quill.root.dispatchEvent(new Event('input', { bubbles: true }));
      emit("update:modelValue", sanitizeHtml(quill.root.innerHTML));
    };

    const toolbarOptions = {
      handlers: {
        link: function (value) {
          if (value) {
            const range = this.quill.getSelection(true);
            if (!range) return;

            let href = prompt("Enter the URL");
            if (!href) return;
            
            // Ensure URL has a protocol
            if (href && !href.match(/^https?:\/\//i)) {
              href = 'https://' + href;
            }

            let img = null;
            
            // Method 1: Check if cursor is positioned at an image
            const [blot] = this.quill.getLeaf(range.index);
            if (blot && blot.domNode && blot.domNode.tagName === 'IMG') {
              img = blot.domNode;
            }
            
            // Method 2: Check if cursor is right after an image
            if (!img && range.index > 0) {
              const [prevBlot] = this.quill.getLeaf(range.index - 1);
              if (prevBlot && prevBlot.domNode && prevBlot.domNode.tagName === 'IMG') {
                img = prevBlot.domNode;
              }
            }
            
            // Method 3: Check if an image is currently selected/focused in the editor
            if (!img) {
              const selection = window.getSelection();
              if (selection && selection.anchorNode) {
                // Check if the selection contains an image
                let node = selection.anchorNode;
                
                // If it's a text node, get its parent
                if (node.nodeType === Node.TEXT_NODE) {
                  node = node.parentElement;
                }
                
                // Check if it's an image and it's within the editor
                if (node && node.tagName === 'IMG' && this.quill.root.contains(node)) {
                  img = node;
                }
                // Note: Removed querySelector logic to avoid selecting unintended images
              }
            }
            
            // Method 4: Check for currently focused image in the editor
            if (!img) {
              const focusedImg = this.quill.root.querySelector('img:focus, img.ql-selected');
              if (focusedImg) {
                img = focusedImg;
              }
            }
            
            if (img) {
              // Image is selected - wrap it in an anchor tag using shared helper
              wrapImageWithLink(img, href);
              
              // Move cursor after the image
              this.quill.setSelection(range.index + 1);
            } else {
              // Regular text selection - apply link format
              this.quill.format("link", href);
            }
          } else {
            this.quill.format("link", false);
          }
        },
        image: function () {
          showImageModal.value = true;
        },
      },
    };

    quill = new Quill(editorContainer.value, {
      theme: "snow",
      placeholder: props.placeholder,
      modules: {
        toolbar: props.customToolbar || [
          [{ header: [1, 2, false] }],
          ["bold", "italic", "underline"],
          ["link", "blockquote"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["image"],
          ["clean"],
        ],
        clipboard: {
          matchVisual: false,
          matchers: [
            [
              "*",
              function (node, delta) {
                const ops = [];
                delta.ops.forEach((op) => {
                  if (op.attributes) {
                    const { color, background, ...keepAttributes } =
                      op.attributes;
                    op.attributes =
                      Object.keys(keepAttributes).length > 0
                        ? keepAttributes
                        : undefined;
                  }
                  ops.push(op);
                });
                return new Delta(ops);
              },
            ],
          ],
        },
        resize: {
          modules: ["Resize", "DisplaySize", "Toolbar"],
          parchment: {
            image: {
              attribute: ["width", "height"],
              limit: {
                minWidth: 100,
                maxWidth: 800,
                minHeight: 100,
                maxHeight: 800,
              },
            },
          },
          styles: {
            handle: {
              backgroundColor: "#000000",
              border: "1px solid #ffffff",
            },
            overlay: {
              border: "1px solid #000000",
            },
          },
        },
      },
    });

    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      if (node.style) {
        node.style.backgroundColor = "";
        node.style.color = "";
      }
      return delta;
    });

    const toolbar = quill.getModule("toolbar");
    toolbar.addHandler("link", toolbarOptions.handlers.link);
    toolbar.addHandler("image", toolbarOptions.handlers.image);

    debouncedHighlightExceededText = createDebouncedHighlight(quill, {
      maxLimit: props.maxLimit,
      enableMaxLimit: props.enableMaxLimit,
      isHighlighting: isHighlighting,
    });

    quill.on("text-change", () => {
      if (isHighlighting.value) return;

      updateCharCount();

      debouncedHighlightExceededText();

      emit(
        "update:modelValue",
        props.isLinkedInStep
          ? quill.getText().trim()
          : sanitizeHtml(quill.root.innerHTML),
      );
    });
    
    // Add click handler for images to make them linkable
    if (!props.disableImageLinking) {
      quill.root.addEventListener('click', (e) => {
        if (e.target.tagName === 'IMG') {
          // Check if image is already wrapped in a link (check all ancestors)
          const existingAnchor = e.target.closest('a');
          const currentHref = existingAnchor ? existingAnchor.href : '';

          const userResponse = confirm(
            existingAnchor
              ? `This image already has a link: ${currentHref}\n\nDo you want to change it?`
              : 'Do you want to add a link to this image?'
          );

          if (userResponse) {
            // Prevent default only after user confirms they want to modify the link
            e.preventDefault();
            let href = prompt('Enter the URL:', currentHref || 'https://');

            if (href) {
              // Ensure URL has a protocol
              if (!href.match(/^https?:\/\//i)) {
                href = 'https://' + href;
              }

              // Use shared helper function to wrap/update image link
              wrapImageWithLink(e.target, href);
            }
          }
        }
      });
    }

    quill.clipboard.addMatcher(Node.TEXT_NODE, (_node, delta) => {
      return delta;
    });

    // Capture clipboard image paste before Quill handles it.
    // Upload to server so the saved note uses a permanent URL, not base64.
    quill.root.addEventListener(
      "paste",
      async (e) => {
        if (!e.clipboardData?.items) return;
        let imageItem = null;
        for (const item of e.clipboardData.items) {
          if (item.type.startsWith("image/")) { imageItem = item; break; }
        }
        if (!imageItem) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const blob = imageItem.getAsFile();
        if (!blob) return;
        const ext = imageItem.type.split("/")[1] || "png";
        const formData = new FormData();
        formData.append("files[]", blob, `pasted-image.${ext}`);
        try {
          const res = await DashboardService.uploadAttachment(formData);
          if (res?.success && res?.files?.[0]?.url) {
            insertImage(res.files[0].url);
          }
        } catch (err) {
          console.error("Failed to upload pasted image:", err);
        }
      },
      true,
    );

    if (props.onblur) {
      quill.root.addEventListener("blur", props.onblur);
    }

    if (props.modelValue) {
      try {
        if (props.isLinkedInStep) {
          quill.setText(props.modelValue || "");
        } else {
          quill.deleteText(0, quill.getLength());
          quill.clipboard.dangerouslyPasteHTML(0, props.modelValue);
        }
      } catch (e) {
        if (props.isLinkedInStep) {
          quill.setText(props.modelValue || "");
        } else {
          quill.root.innerHTML = props.modelValue;
        }
      }
      updateCharCount();
      nextTick(() => {
        highlightExceededText();
      });
    }

    if (props.autoFocus) {
      nextTick(() => {
        quill.focus();
      });
    }

    watch(
      () => props.modelValue,
      (newValue) => {
        if (props.isLinkedInStep) {
          const currentSelection = quill.getSelection();
          const currentText = quill.getText().trim();
          if (newValue !== currentText) {
            quill.setText(newValue || "");
          }
          updateCharCount();
          nextTick(() => {
            highlightExceededText();
          });
          if (currentSelection) {
            nextTick(() => {
              quill.setSelection(currentSelection);
            });
          }
          return;
        }

        const currentSelection = quill.getSelection();
        if (newValue !== sanitizeHtml(quill.root.innerHTML)) {
          try {
            quill.deleteText(0, quill.getLength());
            quill.clipboard.dangerouslyPasteHTML(0, newValue);
          } catch (e) {
            quill.root.innerHTML = newValue;
          }
        }
        updateCharCount();
        nextTick(() => {
          highlightExceededText();
        });
        if (currentSelection) {
          nextTick(() => {
            quill.setSelection(currentSelection);
          });
        }
      },
    );

    nextTick(() => {
      try {
        if (props.isLinkedInStep) {
          quill.setText(props.modelValue || "");
        } else {
          quill.deleteText(0, quill.getLength());
          quill.clipboard.dangerouslyPasteHTML(0, props.modelValue);
        }
      } catch (e) {
        if (props.isLinkedInStep) {
          quill.setText(props.modelValue || "");
        } else {
          quill.root.innerHTML = props.modelValue;
        }
      }
      updateCharCount();
      nextTick(() => {
        highlightExceededText();
      });
    });
  }
});

const insertLink = (url, text) => {
  if (quill) {
    const range = quill.getSelection() || {
      index: quill.getLength(),
      length: 0,
    };
    quill.insertText(range.index, text);
    quill.formatText(range.index, text.length, "link", url);
    quill.setSelection(range.index + text.length);
    emit("update:modelValue", sanitizeHtml(quill.root.innerHTML));
    updateCharCount();
  }
};

defineExpose({
  insertTextAtCursor,
  getPlainText,
  insertHtmlContent,
  insertVideoEmbed,
  getQuill: () => quill,
  insertLink,
  highlightExceededText,
});
</script>

<style>
.quill-editor {
  min-height: 100px;
  width: 100%;
  border: none !important;
  border-radius: 0 0 8px 8px;
}

.ql-editor.ql-blank {
  min-height: 100px;
}

.ql-editor {
  padding: 8px !important;
}

.ql-editor p,
.ql-editor h1,
.ql-editor h2,
.ql-editor h3,
.ql-editor h4,
.ql-editor h5,
.ql-editor h6 {
  margin: 0;
}

.ql-snow .ql-stroke {
  stroke: var(--text-color) !important;
}

.ql-snow .ql-fill,
.ql-snow .ql-stroke.ql-fill {
  fill: var(--text-color) !important;
}

.ql-snow .ql-picker {
  color: var(--text-color) !important;
}

.ql-snow .ql-active .ql-stroke {
  stroke: var(--text-color) !important;
}

.ql-formats * {
  font-family: "Proxima Nova", sans-serif !important;
}

.ql-formats button,
.ql-formats span {
  margin-right: 3px !important;
  margin-left: 3px !important;
}

.ql-formats button {
  opacity: 1 !important;
}

.ql-snow .ql-picker-options {
  background-color: var(--dropdown-bg) !important;
  border: 1px solid var(--quill-editor-border-color) !important;
  border-radius: 8px !important;
}

.ql-snow .ql-picker-label {
  color: var(--text-color) !important;
  padding-left: 0 !important;
}

.ql-snow .ql-picker-item {
  color: var(--text-color) !important;
  padding: 4px !important;
  cursor: pointer;
}

.ql-snow .ql-picker-item:hover,
.ql-snow .ql-picker-item.ql-selected {
  color: #00a8ff !important;
  background-color: var(--white-opacity-20) !important;
  border-radius: 8px !important;
  padding: 4px !important;
}

.ql-snow .ql-picker-label::before {
  border-color: var(--foreground-rgb) transparent transparent transparent !important;
}

.ql-snow .ql-picker.ql-expanded .ql-picker-label {
  border-color: var(--quill-editor-border-color) !important;
}

.ql-snow .ql-picker.ql-expanded .ql-picker-options {
  border-color: var(--quill-editor-border-color) !important;
}

.ql-snow .ql-toolbar button.ql-active,
.ql-snow.ql-toolbar button.ql-active {
  background-color: var(--white-opacity-20) !important;
  border-radius: 4px !important;
}

.ql-snow .ql-toolbar button:hover,
.ql-snow.ql-toolbar button:hover {
  background-color: var(--white-opacity-20) !important;
  border-radius: 4px !important;
}

.ql-snow.ql-toolbar .ql-picker-label:hover {
  background-color: transparent !important;
}

.ql-resize-toolbar button {
  background-color: var(--white-opacity-20) !important;
}

.image-resizer {
  display: block;
  position: absolute;
  border: 1px dashed var(--quill-editor-border-color);
}

.image-resizer .resize-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #2c2c2c;
  border: 1px solid var(--quill-editor-border-color);
}

.image-resizer .ne {
  top: -5px;
  right: -5px;
  cursor: ne-resize;
}

.image-resizer .nw {
  top: -5px;
  left: -5px;
  cursor: nw-resize;
}

.image-resizer .se {
  bottom: -5px;
  right: -5px;
  cursor: se-resize;
}

.image-resizer .sw {
  bottom: -5px;
  left: -5px;
  cursor: sw-resize;
}

.video-embed,
.video-container {
  display: flex !important;
  flex-direction: column !important;
}

.video-embed {
  max-width: 100% !important;
  margin: 8px 0;
}

.video-container {
  max-width: 100% !important;
  width: 100% !important;
  overflow: hidden !important;
}

.video-container img {
  max-width: 100% !important;
  height: auto !important;
  object-fit: contain !important;
}

.email_body_wrapper img,
.ql-editor img {
  max-width: 100% !important;
  height: auto !important;
  object-fit: contain !important;
}

.preview-message-container .video-embed,
.preview-message-container .video-container {
  max-width: 100% !important;
  width: 100% !important;
}

.ql-toolbar.ql-snow .ql-formats {
  margin: 0 !important;
}

.ql-editor li[data-list="bullet"] > .ql-ui:before {
  content: "\2022" !important;
}

.ql-editor ol li[data-list="bullet"]::before {
  content: none !important;
}
</style>
